import React, { useRef } from "react";
import { SearchInputHandle } from "../../../../components/Tables/TableCustomeHandle/TableCustomeHandle.tsx";
import TableCustomeHandle from "../../../../components/Tables/TableCustomeHandle/TableCustomeHandle.tsx";
import { MeetingType } from "../../types/Meeting.tsx";
import dayjs from "dayjs";
import {
  getOrgParamAPI,
  getUserTZAsParam,
} from "../../../../commons/functions/meetingsFunctions.js";
import { objetoAParametrosFetch } from "../../../../components/Tables/helper/fetchParams.ts";

interface MeetingSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectMeeting: (meeting: MeetingType) => void;
  onSelect: () => void;
  token: string;
}

const MeetingSelectionModal: React.FC<MeetingSelectionModalProps> = ({
  isOpen,
  onClose,
  onSelectMeeting,
  onSelect,
  token,
}) => {
  const tablaRef = useRef<any>(null);

  const getMeetingsData = async ({
    page = 1,
    size,
    filters,
    textFilter,
    textFilterFieldName,
  }) => {
    try {
      const url = `${
        process.env.REACT_APP_WHEREBY
      }/get_meetings_by_user_db?page_size=${size}&page_number=${page}&date_from=${dayjs().format(
        "YYYY-MM-DD"
      )}&date_to=${dayjs()
        .add(2, "month")
        .format("YYYY-MM-DD")}${getOrgParamAPI("&")}${getUserTZAsParam("&")}`;

      let queryParams = "";
      if (filters || textFilter) {
        queryParams =
          objetoAParametrosFetch(filters, textFilter, textFilterFieldName) ||
          "";
      }
      const options = {
        method: "GET",
        headers: {
          authorization: "bearer " + token,
        },
      };
      const resp = await fetch(url, options);
      if (resp.status === 401) {
        return new Error("Forbidden");
      }
      if (resp.status === 500) {
        return new Error("Server Error fetching: ordenes diarias");
      }
      const data = await resp.json();
      let listaTemp = data?.meetings;
      return {
        ...data,
        data: listaTemp,
      };
    } catch (error) {
      console.error("Error fetching meetings:", error);
      return undefined;
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      scheduled: { color: "bg-blue-100 text-blue-800", text: "Scheduled" },
      "in-progress": {
        color: "bg-green-100 text-green-800",
        text: "In Progress",
      },
      "No Started": {
        color: "bg-green-100 text-green-800",
        text: "Scheduled",
      },
      completed: { color: "bg-gray-100 text-gray-800", text: "Completed" },
      cancelled: { color: "bg-red-100 text-red-800", text: "Cancelled" },
    };

    const config = statusConfig[status] || {
      color: "bg-gray-100 text-gray-800",
      text: "Scheduled",
    };
    return (
      <span
        className={`px-1 sm:px-2 py-1 rounded-full text-xs font-medium ${config.color}`}
      >
        {config.text}
      </span>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] bg-black bg-opacity-40 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white rounded-lg shadow-lg w-[90%] max-w-[800px] h-[90vh] sm:h-[80vh] flex flex-col relative">
        {/* Header */}
        <div className="flex items-center justify-between pb-2 pt-3 px-3 sm:px-6 border-b">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-gray-800">
              Select Meeting
            </h2>
            <p className="text-xs sm:text-sm text-gray-600">
              Choose a meeting to create an AI social invite
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-xl sm:text-2xl"
          >
            &times;
          </button>
        </div>

        {/* Search */}
        <div className="pt-3 px-3 sm:px-4 justify-end flex">
          <SearchInputHandle
            placeholder="Search meetings..."
            eventName="meeting-selection"
          />
        </div>

        {/* Continue without meeting section */}
        <div className="px-3 sm:px-6 py-2 border-b bg-gray-50">
          <div className="flex flex-row items-center justify-between">
            <p className="text-sm text-gray-600">
              You can continue without a meeting
            </p>
            <button
              onClick={() => onSelect()}
              className="tracking-wide font-semibold bg-brand-primary/90 hover:bg-brand-primary/60 text-gray-800 px-3 py-1.5 rounded-md text-xs sm:text-sm transition-colors"
            >
              Continue without meeting
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-hidden p-2 sm:p-4">
          <div className="overflow-x-auto">
            <TableCustomeHandle
              ref={tablaRef}
              dataFetch={getMeetingsData}
              rowsPerPageOptions={[10, 20, 50]}
              searchName="meeting-selection"
              containerClassName="h-full min-w-full"
              isList
            >
              {(meeting: MeetingType, index: number) => (
                <div
                  key={meeting._id}
                  className="w-full border rounded-lg px-4 py-2 mb-2 bg-white shadow hover:shadow-md transition flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
                >
                  {/* Columna: Título + descripción */}
                  <div className="flex-1 min-w-[200px] mr-2">
                    <div className="text-sm sm:text-base font-semibold text-gray-800 leading-tight">
                      {meeting.topic || "No Title"}
                    </div>
                    {meeting.description && (
                      <div className="text-xs sm:text-sm text-gray-600 line-clamp-2 leading-tight">
                        {meeting.description}
                      </div>
                    )}
                  </div>

                  {/* Columna: ID */}
                  <div className="flex flex-col items-start  sm:w-[80px] text-xs sm:text-sm font-mono text-gray-500">
                    <span>ID.</span>
                    <span>{meeting.meetingId || "N/A"}</span>
                  </div>

                  {/* Columna: Fecha + hora + estado */}
                  <div className="flex flex-col items-start sm:items-center sm:w-[180px] text-xs sm:text-sm text-gray-600">
                    {meeting.startDateM ? (
                      <>
                        <div>
                          {dayjs(meeting.startDateM).format(
                            "MMM DD, YYYY [at] hh:mm A"
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="text-gray-400">No Date</div>
                    )}
                    <div className="mt-1">
                      {getStatusBadge(
                        meeting.status_meeting
                          ? meeting.status_meeting
                          : "scheduled"
                      )}
                    </div>
                  </div>

                  {/* Columna: Acción */}
                  <div className="sm:w-[120px]">
                    <button
                      onClick={() => onSelectMeeting(meeting)}
                      className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium !leading-none"
                    >
                      <span className="hidden sm:inline">
                        Create About This Meeting
                      </span>
                      <span className="sm:hidden">Create</span>
                    </button>
                  </div>
                </div>
              )}
            </TableCustomeHandle>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeetingSelectionModal;
